import React, { useState, useMemo } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Package, Activity } from 'lucide-react';
import { DailyMetalData } from '../types/metal';
import { formatNumberDisplay } from '@/lib/utils';

interface MetalFinishedChartsProps {
  dailyData: DailyMetalData[];
  isLoading: boolean;
  error: string | null;
  title: string;
}

const MetalFinishedCharts: React.FC<MetalFinishedChartsProps> = ({ 
  dailyData, 
  isLoading, 
  error, 
  title 
}) => {
  const [activeMetric, setActiveMetric] = useState<'m3Carton' | 'containerCount'>('m3Carton');

  // Transform data for charts
  const chartData = useMemo(() => {
    return dailyData.map(day => ({
      date: day.date,
      m3Carton: day.m3Carton || 0,
      containerCount: day.containerCount || 0,
    }));
  }, [dailyData]);

  // Calculate average values for trendlines
  const averageValue = useMemo(() => {
    if (!chartData.length) return 0;
    
    // Calculate working days (days where the selected metric > 0)
    const workingDays = chartData.filter(day => day[activeMetric] > 0);

    if (workingDays.length === 0) return 0;

    const sum = workingDays.reduce((acc, day) => acc + day[activeMetric], 0);
    return sum / workingDays.length;
  }, [chartData, activeMetric]);

  // Get unit for metrics
  const getUnit = () => {
    switch (activeMetric) {
      case 'm3Carton': return 'M³';
      case 'containerCount': return 'Container';
      default: return '';
    }
  };

  // Get title for metrics
  const getTitle = () => {
    switch (activeMetric) {
      case 'm3Carton': return 'M³ Carton';
      case 'containerCount': return 'Số Container';
      default: return '';
    }
  };

  // Get color for metrics
  const getColor = () => {
    switch (activeMetric) {
      case 'm3Carton': return '#3b82f6'; // Blue
      case 'containerCount': return '#f97316'; // Orange
      default: return '#3b82f6';
    }
  };

  // Format numbers
  // Use Vietnamese locale formatting for charts

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-600">Lỗi tải biểu đồ: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            {title}
          </div>
          
          {/* Metric Selection Buttons - Top Right */}
          <div className="flex flex-wrap items-center gap-2 justify-end md:justify-start">
            <Button
              variant={activeMetric === 'm3Carton' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveMetric('m3Carton')}
              className="flex items-center gap-1"
            >
              <Package className="h-4 w-4" />
              M³ Carton
            </Button>
            
            <Button
              variant={activeMetric === 'containerCount' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveMetric('containerCount')}
              className="flex items-center gap-1"
            >
              <Activity className="h-4 w-4" />
              Số Container
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-80 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : chartData.length > 0 ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(date) => {
                    const d = new Date(date);
                    return `${d.getDate()}/${d.getMonth() + 1}`;
                  }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => formatNumberDisplay(value, 0)}
                />
                <Tooltip 
                  formatter={(value: any) => [
                    `${formatNumberDisplay(value, activeMetric === 'containerCount' ? 1 : 2)} ${getUnit()}`, 
                    getTitle()
                  ]}
                  labelFormatter={(date) => {
                    const d = new Date(date);
                    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
                  }}
                />
                
                {/* Data Line */}
                <Line
                  type="monotone"
                  dataKey={activeMetric}
                  stroke={getColor()}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                />
                
                {/* Average Reference Line */}
                <ReferenceLine
                  y={averageValue}
                  stroke={getColor()}
                  strokeDasharray="8 8"
                  strokeWidth={1}
                  opacity={0.7}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-80 flex items-center justify-center">
            <p className="text-gray-500">Không có dữ liệu biểu đồ</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MetalFinishedCharts;